/**
 * Comments effects — side-effect runner.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * Elm-ish architecture: effects are the ONLY place with side effects.
 * - API calls
 * - Navigation
 * - Browser APIs
 *
 * Effects are queued by the reducer and executed outside it.
 */
import type { CommentsEffect } from './update';
import type { CommentsMsg } from './messages';
import type { AuthorSummary, Comment } from './model';

// =============================================================================
// Safe user-facing error messages
// =============================================================================

/**
 * Safe user-facing messages — no internal details leaked.
 */
const SAFE_ERROR_MESSAGES = {
  unavailable: 'Сервис комментариев временно недоступен. Попробуйте позже.',
  unexpected: 'Не удалось загрузить комментарии. Попробуйте ещё раз.',
  submit_failed: 'Не удалось отправить комментарий. Попробуйте ещё раз.',
  like_failed: 'Не удалось обновить. Попробуйте ещё раз.',
} as const;

/**
 * Map an unknown error to a safe generic message.
 * Never expose err.message, stack, or response body.
 */
function mapUnknownErrorToSafe(_err: unknown): string {
  return SAFE_ERROR_MESSAGES.unavailable;
}

// =============================================================================
// Dependencies interface
// =============================================================================

/**
 * Dependencies interface for comments effects.
 *
 * The page or application composition root owns the concrete adapters.
 */
export interface CommentsDependencies {
  /**
   * Load post and comments data.
   */
  readonly loadComments: {
    load(postId: string, signal: AbortSignal): Promise<LoadCommentsResult>;
  };
  /**
   * Submit a new comment.
   */
  readonly submitComment: {
    submit(
      postId: string,
      content: string,
      signal: AbortSignal
    ): Promise<SubmitCommentResult>;
  };
  /**
   * Toggle like on a comment.
   */
  readonly toggleLike: {
    toggle(commentId: string, signal: AbortSignal): Promise<ToggleLikeResult>;
  };
  /**
   * Load current user profile for composer avatar.
   */
  readonly loadCurrentUser: {
    load(signal: AbortSignal): Promise<AuthorSummary | null>;
  };
}

// =============================================================================
// Result types (mirroring feed-composer pattern)
// =============================================================================

/**
 * Result type for loading comments.
 */
export type LoadCommentsResult =
  | { readonly kind: 'found'; readonly post: import('./model').RootPost; readonly comments: readonly Comment[] }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'unexpected_failure' };

/**
 * Result type for submitting a comment.
 */
export type SubmitCommentResult =
  | { readonly kind: 'created'; readonly comment: Comment }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'unexpected_failure' };

/**
 * Result type for toggling a like.
 */
export type ToggleLikeResult =
  | { readonly kind: 'success'; readonly liked: boolean; readonly count: number }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'unexpected_failure' };

// =============================================================================
// Effect context
// =============================================================================

/**
 * Context passed to runEffect so effects can dispatch messages back.
 */
export interface CommentsEffectContext {
  dispatch: (msg: CommentsMsg) => void;
}

// =============================================================================
// Effect runners
// =============================================================================

/**
 * Run a single effect with cancellation support.
 *
 * @param effect — The effect to run
 * @param ctx — Effect context for dispatching messages
 * @param deps — Injected dependencies
 * @param signal — AbortSignal for cancellation
 */
export async function runEffect(
  effect: CommentsEffect,
  ctx: CommentsEffectContext,
  deps: CommentsDependencies,
  signal: AbortSignal
): Promise<void> {
  switch (effect.type) {
    case 'LoadComments': {
      if (signal.aborted) return;
      try {
        const result = await deps.loadComments.load(effect.postId, signal);
        if (signal.aborted) return;
        if (result.kind === 'found') {
          ctx.dispatch({
            type: 'LoadSucceeded',
            loadId: effect.loadId,
            post: result.post,
            comments: result.comments,
          });
        } else if (result.kind === 'not_found') {
          ctx.dispatch({ type: 'LoadNotFound', loadId: effect.loadId });
        } else {
          ctx.dispatch({
            type: 'LoadFailed',
            loadId: effect.loadId,
            error: SAFE_ERROR_MESSAGES.unavailable,
          });
        }
      } catch (err) {
        if (signal.aborted) return;
        ctx.dispatch({
          type: 'LoadFailed',
          loadId: effect.loadId,
          error: mapUnknownErrorToSafe(err),
        });
      }
      break;
    }

    case 'LoadCurrentUser': {
      if (signal.aborted) return;
      try {
        const user = await deps.loadCurrentUser.load(signal);
        if (signal.aborted) return;
        ctx.dispatch({ type: 'CurrentUserLoaded', loadId: effect.loadId, user });
      } catch (_err) {
        if (signal.aborted) return;
        ctx.dispatch({ type: 'CurrentUserLoaded', loadId: effect.loadId, user: null });
      }
      break;
    }

    case 'SubmitComment': {
      if (signal.aborted) return;
      const { submissionId, postId, content } = effect;
      try {
        const result = await deps.submitComment.submit(postId, content, signal);
        if (signal.aborted) return;
        if (result.kind === 'created') {
          ctx.dispatch({
            type: 'SendSucceeded',
            submissionId,
            comment: result.comment,
          });
        } else {
          ctx.dispatch({
            type: 'SendFailed',
            submissionId,
            error: SAFE_ERROR_MESSAGES.submit_failed,
          });
        }
      } catch (err) {
        if (signal.aborted) return;
        ctx.dispatch({
          type: 'SendFailed',
          submissionId,
          error: mapUnknownErrorToSafe(err),
        });
      }
      break;
    }

    case 'ToggleLike': {
      if (signal.aborted) return;
      const { commentId } = effect;
      try {
        const result = await deps.toggleLike.toggle(commentId, signal);
        if (signal.aborted) return;
        if (result.kind === 'success') {
          ctx.dispatch({
            type: 'LikeSucceeded',
            commentId,
            liked: result.liked,
            count: result.count,
          });
        } else {
          ctx.dispatch({ type: 'LikeFailed', commentId });
        }
      } catch (err) {
        if (signal.aborted) return;
        ctx.dispatch({ type: 'LikeFailed', commentId });
      }
      break;
    }
  }
}
